import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { Badge, Button, Card, InfoTip, Input, Notice, fmtDateTime, fmtPhone, formatUsd } from '@rydres/ui';
import { adminApi, chatApi } from '../lib/api.js';
import type { AdminDriver, AdminStats } from '../lib/types.js';

const planTone = (s: string) => (s === 'active' || s === 'trialing' || s === 'comped' ? 'ok' : s === 'past_due' ? 'warn' : 'muted');

export function Admin() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [drivers, setDrivers] = useState<AdminDriver[]>([]);
  const [search, setSearch] = useState('');
  useEffect(() => {
    adminApi.stats().then(setStats, () => undefined);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => adminApi.drivers(search || undefined).then((r) => setDrivers(r.drivers), () => undefined), 250);
    return () => clearTimeout(t);
  }, [search]);
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Admin</h1>
      <Reports />
      {stats && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            ['Drivers', stats.drivers.drivers],
            ['Pages live', stats.drivers.live],
            ['Paying', stats.drivers.paying],
            ['Comped', stats.drivers.comped],
            ['Bookings, 30d', stats.last30Days.bookings],
            ['Confirmed, 30d', stats.last30Days.confirmed],
            ['Gross, 30d', formatUsd(stats.last30Days.grossCents)],
            ['Fees, 30d', formatUsd(stats.last30Days.feeCents)],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-2xl border border-line bg-card px-4 py-3">
              <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
              <div className="mt-1 text-xl font-black">{value}</div>
            </div>
          ))}
        </div>
      )}
      <Input placeholder="Search name, handle, TCP, phone" value={search} onChange={(e) => setSearch(e.target.value)} />
      <div className="space-y-2">
        {drivers.map((d) => (
          <Link key={d.id} to={`/admin/drivers/${d.id}`} className="flex items-center justify-between rounded-2xl border border-line bg-card p-3 text-sm">
            <div>
              <div className="font-bold">{d.displayName ?? fmtPhone(d.phone)}{d.handle ? <span className="font-medium text-muted"> · /{d.handle}</span> : ''}</div>
              <div className="text-xs text-muted">{d.legalName ?? '—'} · {d.tcpNumber ?? 'no TCP'} · joined {new Date(d.createdAt).toLocaleDateString()}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge value={d.planStatus} tone={planTone(d.planStatus)} />
              <Badge value={d.pageLive ? 'live' : 'not live'} tone={d.pageLive ? 'ok' : 'muted'} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function AdminDriverScreen() {
  const { id = '' } = useParams();
  const [d, setD] = useState<Awaited<ReturnType<typeof adminApi.driver>> | null>(null);
  const [months, setMonths] = useState('3');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const load = () => adminApi.driver(id).then(setD, () => undefined);
  useEffect(() => void load(), [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p className="text-sm text-muted">Loading…</p>;
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="rounded-full bg-card2 p-2 px-3">‹</Link>
        <h1 className="text-xl font-black">{d.displayName ?? fmtPhone(d.phone)}</h1>
        <Badge value={d.planStatus} tone={planTone(d.planStatus)} />
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      <Card>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">Phone</dt><dd>{fmtPhone(d.phone)}</dd>
          <dt className="text-muted">Page</dt><dd>{d.handle ? <a className="text-accent-text underline" href={`https://rydres.com/${d.handle}`} target="_blank" rel="noreferrer">rydres.com/{d.handle}</a> : '—'} · {d.pageLive ? 'live' : 'not live'}</dd>
          <dt className="text-muted">Business</dt><dd>{d.legalName ?? '—'} · {d.tcpNumber ?? '—'}</dd>
          <dt className="text-muted">Stripe</dt><dd>{d.stripeChargesEnabled ? 'card payments enabled' : 'not enabled'}</dd>
          {d.compedUntil && <><dt className="text-muted">Comped until</dt><dd>{fmtDateTime(d.compedUntil)}</dd></>}
        </dl>
      </Card>
      <Card title="Complimentary plan">
        {d.planStatus === 'comped' ? (
          <Button variant="danger" small onClick={() => void act(() => adminApi.uncomp(d.id))}>End the comp</Button>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <label className="block text-sm"><span className="mb-1 block text-xs font-bold uppercase text-muted">Months</span><Input type="number" min={1} max={24} value={months} onChange={(e) => setMonths(e.target.value)} className="w-24" /></label>
            <label className="block flex-1 text-sm"><span className="mb-1 block text-xs font-bold uppercase text-muted">Note</span><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Pilot driver, onboarded at LAX" /></label>
            <Button small onClick={() => void act(() => adminApi.comp(d.id, new Date(Date.now() + Number(months) * 30 * 86_400_000).toISOString(), note || undefined))}>Comp</Button>
          </div>
        )}
      </Card>
      <Card title={`Services (${d.services.length})`}>
        <ul className="text-sm">{d.services.map((s) => <li key={s.id} className="flex justify-between py-1"><span>{s.name}</span><span className="font-bold">{formatUsd(s.priceCents)}</span></li>)}</ul>
      </Card>
      <Card title="Recent bookings">
        <ul className="divide-y divide-line text-sm">
          {d.bookings.length === 0 && <li className="py-2 text-muted">None yet.</li>}
          {d.bookings.map((b) => (
            <li key={b.id} className="flex items-center justify-between py-2">
              <span>{fmtDateTime(b.startsAt)} · {b.riderName} · {b.kind}</span>
              <span className="flex items-center gap-2">{b.priceCents != null ? formatUsd(b.priceCents) : '—'} <Badge value={b.status} /></span>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Audit">
        <ul className="text-xs text-muted">{d.audit.map((a, i) => <li key={i}>{fmtDateTime(a.createdAt)} · {a.action}</li>)}</ul>
      </Card>
    </div>
  );
}

type ReportCopy = Awaited<ReturnType<typeof chatApi.report>>;

/** Conversations a driver or rider reported, between a driver and a rider or two drivers; reading one is written to the audit log. */
function Reports() {
  const [list, setList] = useState<Awaited<ReturnType<typeof chatApi.reports>>['reports'] | null>(null);
  const [open, setOpen] = useState<ReportCopy | null>(null);
  useEffect(() => {
    chatApi.reports().then((r) => setList(r.reports), () => setList([]));
  }, []);
  if (!list?.length) return null;
  return (
    <section className="space-y-2 rounded-2xl border border-line bg-card p-4">
      <h2 className="flex items-center gap-1.5 font-bold">
        Reported conversations
        <InfoTip label="Reported conversations">Reading a report is recorded in the audit log.</InfoTip>
      </h2>
      <ul className="space-y-2">
        {list.map((r) => (
          <li key={r.id} className="space-y-1 rounded-xl bg-card2 p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold">
                {r.kind === 'friend' ? 'Between two drivers' : 'Driver and rider'} · reported by the {r.reportedBy === 'passenger' ? 'rider' : 'driver'} · {fmtDateTime(r.at)} · {r.messages} messages
              </span>
              <button className="shrink-0 font-bold text-accent-text" onClick={() => void chatApi.report(r.id).then(setOpen, () => undefined)}>Read</button>
            </div>
            {r.reason && <p className="text-ink2">{r.reason}</p>}
            {open?.id === r.id && (
              <ol className="space-y-1 pt-1">
                {open.messages.map((m, i) => (
                  <li key={i}>
                    <span className="font-bold">{m.sender === 'passenger' ? 'Rider' : (open.names?.[m.sender as 'driver' | 'friend'] ?? 'Driver')}:</span> {m.body} <span className="text-xs text-muted">{fmtDateTime(m.at)}</span>
                  </li>
                ))}
              </ol>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
